/* ==========================================================================
   DRAWER SEARCH ON A PHONE

   Two iOS behaviours Thomas hit:

   1. "if you press tick to say 'finished typing' on ios, its closing the search
      bar". The tick dismisses the keyboard, which blurs the input. Dawn's
      predictive-search closes its results on focusout, and it cannot tell a
      dismissed keyboard from the user tapping somewhere else - in both cases
      activeElement lands on <body>. The difference IS visible in the event
      though: a real tap elsewhere sets relatedTarget, a dismissed keyboard
      leaves it null. So that one case is stopped before Dawn's handler sees it.

   2. "if i keep the keyboard open, it covers the search". The drawer is a fixed
      panel, so iOS does not scroll it to reveal the focused field, and the
      results run underneath the keyboard. visualViewport reports the area the
      keyboard has NOT taken, so the panel is capped to what is actually
      visible and the field is brought to the top of the drawer's scroller.

   Everything here is guarded: no visualViewport, no listeners; no search in the
   drawer, nothing runs.
   ========================================================================== */

(function () {
  var SEARCH = '.stk-drawer-search';

  function wrap() {
    return document.querySelector(SEARCH);
  }

  /* --- 1. a dismissed keyboard must not close the results ---------------- */
  document.addEventListener(
    'focusout',
    function (event) {
      var w = wrap();
      if (!w || !w.contains(event.target)) return;

      /* Focus moved somewhere else on purpose - let Dawn close as it likes. */
      if (event.relatedTarget) return;

      /* Nothing took focus: the keyboard was dismissed. With a typed query the
         results stay up. With an EMPTY field the whole search folds back to its
         icon instead - and that has to happen HERE, because the stopPropagation
         below kills the event before any later listener would see it. That is
         exactly what silently disabled the first collapse-on-blur attempt. */
      var emptyInput = event.target.closest('.stk-drawer-search')
        ? event.target.closest('.stk-drawer-search').querySelector('input[type="search"]')
        : null;
      if (emptyInput && emptyInput.value.trim() === '' && window.__stkCollapseSearch) {
        window.__stkCollapseSearch();
        return;
      }
      event.stopPropagation();
    },
    true
  );

  /* --- clearing the field must close the results ------------------------
     The reset button is type="reset", which empties the input without firing an
     `input` event - so predictive-search never hears that the term went away and
     the panel stays up covering the whole menu. Thomas: "when you press the x on
     the search clear after youve searched or typed. it leave the results open
     hiding all the menu items."

     The component's own close() is used rather than hiding the panel, so its
     aria-expanded, its status region and its cached results all reset the way
     they would if the user had deleted the text by hand. */
  document.addEventListener('click', function (event) {
    var w = wrap();
    if (!w || !event.target.closest(SEARCH + ' .reset__button')) return;

    /* After the browser's own reset has run, not before it. */
    setTimeout(function () {
      var input = w.querySelector('input[type="search"]');
      var ps = w.querySelector('predictive-search');

      if (ps && typeof ps.close === 'function') {
        ps.close(true);
      } else if (input) {
        /* Predictive search switched off in theme settings. */
        input.value = '';
      }

      /* The x empties the field, so the search folds back to the icon row and
         account and home return - on every pointer type, per Thomas: "if you
         type nothing in search or click the little x... it closes back down to
         reveal account and home." */
      if (window.__stkCollapseSearch) window.__stkCollapseSearch();

      /* AND THEN LET GO OF THE FIELD, ON TOUCH ONLY.

         Dawn puts focus back in the input after a reset so you can carry on
         typing. With a mouse that is a courtesy; with a touch keyboard it means
         clearing the field summons the keyboard straight back and you have to
         dismiss it a second time - Thomas: "it still re-opens the keyboard which
         you then need to close again."

         pointer: coarse is the test rather than a screen width, because the
         thing that matters is whether focus brings up a keyboard, not how wide
         the screen is. On a desktop the focus stays exactly as Dawn intends.

         Deferred one more frame: Dawn's own focus() call runs inside close(),
         so blurring in the same tick would be undone by it. */
      if (!window.matchMedia('(pointer: coarse)').matches) return;

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (input && document.activeElement === input) input.blur();
        });
      });
    }, 0);
  });

  /* --- 2. keep the field and its results above the keyboard -------------- */
  function fit() {
    var w = wrap();
    if (!w) return;
    var vpH = window.visualViewport ? window.visualViewport.height : window.innerHeight;

    var panel = w.querySelector('.predictive-search');
    if (!panel) return;

    /* Measured from the PANEL's own top, not the field - a cached 782px cap
       proved a measurement taken mid-animation sticks forever if it is the last
       one to run. The panel's rect is correct whenever the panel is actually
       showing, and the call below re-runs after results render, so a bad early
       number is always overwritten by a good late one. */
    var panelTop = panel.getBoundingClientRect().top;
    if (panelTop <= 0) return;
    panel.style.maxHeight = Math.max(160, vpH - panelTop - 16) + 'px';
  }

  document.addEventListener('focusin', function (event) {
    var w = wrap();
    if (!w || !w.contains(event.target)) return;

    /* The keyboard animates in over roughly 300ms; measuring before it settles
       gives the pre-keyboard height and no correction at all. */
    setTimeout(fit, 340);
  });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', fit);
    window.visualViewport.addEventListener('scroll', fit);
  }

  /* The panel opens on input, not only on focus - and with no keyboard (or one
     already dismissed) nothing else would size it. */
  document.addEventListener('input', function (event) {
    var w = wrap();
    if (!w || !w.contains(event.target)) return;
    /* Twice: once for the immediate layout, once after the fetched results
       have actually rendered and the panel has its real position. */
    setTimeout(fit, 60);
    setTimeout(fit, 700);
  });
})();


/* ==========================================================================
   THE BAR SEARCH TOGGLE

   Open puts focus in the field so the keyboard comes straight up; close goes
   through predictive-search's own close() so its state resets properly. The
   drawer's toggle event shuts the search with the menu, so it can never be
   left open over a page with no menu.
   ========================================================================== */

(function () {
  function panel() {
    return document.getElementById('StkBarSearchPanel');
  }

  function shut(pl) {
    var ps = pl.querySelector('predictive-search');
    if (ps && typeof ps.close === 'function') ps.close(true);
    pl.setAttribute('hidden', '');
    var t = document.querySelector('.stk-bar-search__toggle');
    if (t) t.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', function (event) {
    var t = event.target.closest('.stk-bar-search__toggle');
    if (!t) return;
    var pl = panel();
    if (!pl) return;

    if (pl.hasAttribute('hidden')) {
      pl.removeAttribute('hidden');
      t.setAttribute('aria-expanded', 'true');
      var input = pl.querySelector('input[type="search"]');
      if (input) input.focus();
    } else {
      shut(pl);
    }
  });

  var det = document.getElementById('Details-menu-drawer-container');
  if (det) {
    det.addEventListener('toggle', function () {
      var pl = panel();
      if (!det.open && pl && !pl.hasAttribute('hidden')) shut(pl);
    });
  }
})();


/* ==========================================================================
   BOTTOM-SHEET GESTURES FOR THE MENU

   Tap on the dimmed page closes the sheet; drag the grab bar down past 80px
   and it closes, short drags spring back. Both go through the summary's own
   click so Dawn's open/close logic (focus trap, aria-expanded, animation
   classes) runs exactly as if the X had been tapped.
   ========================================================================== */

(function () {
  var det = document.getElementById('Details-menu-drawer-container');
  if (!det) return;
  var summary = det.querySelector('summary');

  /* The overlay is a ::before on the details, so an outside tap's target IS the
     details element - anything inside the sheet targets a descendant instead. */
  document.addEventListener('click', function (event) {
    if (det.open && event.target === det && summary) summary.click();
  });

  /* Cart over menu is two sheets stacked. Thomas: "tapping cart whilst you're
     in hamburger, opens cart over the hamburger. it should auto close menu and
     open cart instead." The menu closes first; the cart click proceeds
     untouched, so whatever the cart icon does - drawer or page - is unchanged. */
  document.addEventListener(
    'click',
    function (event) {
      if (!det.open) return;
      if (!event.target.closest('#cart-icon-bubble')) return;
      if (summary) summary.click();
    },
    true
  );

  var handle = det.querySelector('.stk-sheet-handle');
  var sheet = det.querySelector('.menu-drawer');
  if (!handle || !sheet) return;

  var startY = null;

  handle.addEventListener('pointerdown', function (event) {
    startY = event.clientY;
    sheet.style.transition = 'none';
    handle.setPointerCapture(event.pointerId);
  });

  handle.addEventListener('pointermove', function (event) {
    if (startY === null) return;
    var dy = Math.max(0, event.clientY - startY);
    sheet.style.transform = 'translate3d(0, ' + dy + 'px, 0)';
  });

  function release(event) {
    if (startY === null) return;
    var dy = Math.max(0, event.clientY - startY);
    startY = null;
    sheet.style.transition = '';
    sheet.style.transform = '';
    if (dy > 80 && summary) summary.click();
  }

  handle.addEventListener('pointerup', release);
  handle.addEventListener('pointercancel', release);
})();


/* ==========================================================================
   THE SEARCH ICON EXPANDS INTO THE FIELD

   Tap the magnifier: the three icons step aside, the field takes the row and
   focuses so the keyboard comes straight up. It collapses back to icons when
   the field loses focus while EMPTY, and whenever the menu closes - but never
   while there is a typed query, so a dismissed keyboard cannot eat a search.
   ========================================================================== */

(function () {
  function bits() {
    var top = document.querySelector('[data-stk-top]');
    var wrapEl = document.getElementById('StkDrawerSearchWrap');
    return top && wrapEl ? { top: top, wrap: wrapEl, input: wrapEl.querySelector('input[type="search"]') } : null;
  }

  function expand() {
    var b = bits();
    if (!b) return;
    b.wrap.removeAttribute('hidden');
    b.top.classList.add('stk-drawer-top--search');
    var t = b.top.querySelector('.stk-drawer-searchtoggle');
    if (t) t.setAttribute('aria-expanded', 'true');
    if (b.input) b.input.focus();
  }

  /* Reachable from the keyboard-dismissal guard above, which owns the focusout
     event before anything else does. */
  window.__stkCollapseSearch = collapse;

  function collapse() {
    var b = bits();
    if (!b) return;
    var ps = b.wrap.querySelector('predictive-search');
    if (ps && typeof ps.close === 'function') ps.close(true);
    b.wrap.setAttribute('hidden', '');
    b.top.classList.remove('stk-drawer-top--search');
    var t = b.top.querySelector('.stk-drawer-searchtoggle');
    if (t) t.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('.stk-drawer-searchtoggle')) {
      expand();
      return;
    }
    /* A tap anywhere OUTSIDE the open search folds it away - Thomas: "if you
       just click elsewhere on the screen it closes keyboard but not the search
       field." The keyboard tick keeps a typed query (that path is the focusout
       guard); an actual tap elsewhere is the user leaving, so the field goes
       with the keyboard whether or not it has text. */
    var b = bits();
    if (!b || b.wrap.hasAttribute('hidden')) return;
    if (event.target.closest('#StkDrawerSearchWrap')) return;
    collapse();
  }, true);
  /* ^ capture, because Dawn's submenu summaries stop the click before it would
     ever bubble back to the document - measured: a tap on SUPPLIES left the
     field open. In capture this runs first, folds the search, and the tap then
     does whatever it was going to do. */

  /* Empty and left: fold back to icons. With text in it, stay - the keyboard
     being dismissed must not throw the query away. */
  document.addEventListener('focusout', function (event) {
    var b = bits();
    if (!b || b.wrap.hasAttribute('hidden')) return;
    if (!b.wrap.contains(event.target)) return;
    if (event.relatedTarget && b.wrap.contains(event.relatedTarget)) return;
    if (b.input && b.input.value.trim() === '') collapse();
  });

  var det = document.getElementById('Details-menu-drawer-container');
  if (det) {
    det.addEventListener('toggle', function () {
      if (!det.open) collapse();
    });
  }
})();
